// Import dependencies
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

// eslint-disable-next-line require-jsdoc
const LogItemMixin = ( superClass ) => class extends superClass {
	// Properties
	@property( { type: Number, attribute: 'item-id' } ) accessor id;

	@property( { type: String, attribute: 'item-code' } ) accessor code;

	@property( { type: String, attribute: 'integration-code' } ) accessor integrationCode;

	@property( { type: String, attribute: 'status-id' } ) accessor statusID;

	@property( { type: String, attribute: 'start-date' } ) accessor startDate;

	@property( { type: String, attribute: 'end-date' } ) accessor endDate = this.startDate;

	@property( { type: Boolean, attribute: 'is-open' } ) accessor isOpen = false;

	// Check if the data is coming from an old version of Automator
	@property( { type: Boolean, attribute: 'from-legacy-log' } ) accessor fromLegacyLog = false;

	// This property is used to indicate to the web component that it shouldn't try to retrieve data from the server
	// and that it should use the data provided when it was created
	@property( { type: Boolean, attribute: 'import-mode' } ) accessor isImportMode = false;

	@property( { type: String, attribute: false } ) accessor titleHTML = '';

	@property( { type: String, attribute: false } ) accessor titleCustomName = '';

	@property( { type: Array, attribute: false } ) accessor fields = [];

	@property( { type: Array, attribute: false } ) accessor runs = [];

	// eslint-disable-next-line require-jsdoc
	render() {
		// Check if the data is missing
		if ( this.isDataMissing ) {
			return this._templateMissingData;
		}

		return html`
			<div 
				class="item item--${ this.statusID }"
			>
				${ this._templateIntegrationIcon }
				${ this._templateSentence }

				<div class="item__details">
					${ this._templateItemID }
					${ this._templateDate }
					${ this._templateRuns }
					${ this._templateStatus }
				</div>

				${ this._templateOpenSidebarButton }
			</div>
		`;
	}

	/**
	 * Keeps the sidebar up to date
	 * Called before update() to compute values needed during the update.
	 * We'll use this method to keep the sidebar up to date when one the reactive properties of the action change
	 *
	 * @param {Map} changedProperties The properties that changed
	 */
	willUpdate( changedProperties ) {
		// Check if the property that changed was .isOpen
		// If that's the case, WE DON'T NEED TO DO THIS HERE AGAIN
		// When the property .isOpen changes, `.requestSidebar()` is invoked, which
		// is already sending the request to the main component to update the sidebar data
		if ( changedProperties.has( 'isOpen' ) ) {
			return;
		}

		// Check if it's open
		if ( ! this.isOpen ) {
			return;
		}

		// Request sidebar update
		this.updateSidebar();
	}

	/**
	 * Opens the sidebar to show more data about this item
	 */
	requestSidebar() {
		if ( this.isOpen ) {
			// Close it
			this.dispatchEvent(
				new CustomEvent( 'uap-log-request-sidebar', {
					bubbles: true,
					composed: true,
					detail: {
						action: 'close',
					}
				} )
			);

			return;
		}

		this.updateSidebar();
	}

	/**
	 * Updates the sidebar data
	 */
	updateSidebar() {
		this.dispatchEvent(
			new CustomEvent( 'uap-log-request-sidebar', {
				bubbles: true,
				composed: true,
				detail: {
					action: 'open',
					sourceID: `item-${ this.id }`,
					content: this.sidebarContent
				}
			} )
		);
	}

	/**
	 * Renders the integration icon
	 *
	 * @return {TemplateResult} The integration icon
	 */
	get _templateIntegrationIcon() {
		return html`
			<div class="item__icon">
				<uap-log-integration-icon size="16" integration-id="${ this.integrationCode }" show-tooltip></uap-log-integration-icon>
			</div>
		`;
	}

	/**
	 * Renders the item sentence
	 *
	 * @return {TemplateResult} The sentence
	 */
	get _templateSentence() {
		return html`
			<div class="item__sentence">
				<uap-log-sentence 
					.sentence="${ this.titleHTML }"
					custom-name="${ this.titleCustomName }"
				></uap-log-sentence>
			</div>
		`;
	}

	/**
	 * Renders the template used when the basic data of the trigger/action is missing
	 *
	 * @return {TemplateResult} The template used when the basic data of the trigger/action is missing
	 */
	get _templateMissingData() {
		return html`
			<div class="item item--missing">
				<div class="item__icon">
					<uap-log-integration-icon size="16" show-tooltip></uap-log-integration-icon>
				</div>

				<div class="item__plain-message">
					${ this.dataMissingErrorMessage }
				</div>
			</div>
		`;
	}

	/**
	 * Returns the template of the button that requests the sidebar
	 *
	 * @return {TemplateResult|undefined} The button or undefined if the button shouldn't be shown
	 */
	get _templateOpenSidebarButton() {
		// Check if we shouldn't add the button at all
		if ( [ 'not-completed' ].includes( this.statusID ) ) {
			return;
		}

		// Skipped actions that have ran before Automator 5.0 contain the conditions message inside the actions notes
		// So we have to show the sidebar in that case even though there are no fields
		if ( this.statusID === 'skipped' && ! this.fromLegacyLog ) {
			return;
		}

		return html`
			<uo-tooltip class="item__more-info">
				${ ! this.isOpen ? escapeHTML( __( 'Open in sidebar', 'uncanny-automator' ) ) : escapeHTML( __( 'Close sidebar', 'uncanny-automator' ) ) }

				<uo-button 
					slot="target"
					color="transparent" 
					@click=${ this.requestSidebar }
				>
					<uo-icon id="${ this.isOpen ? 'angle-left' : 'angle-right' }"></uo-icon>
				</uo-button>
			</uo-tooltip>
			
		`;
	}

	/**
	 * Returns the HTML of the section that shows the item ID
	 *
	 * @return {TemplateResult} The item ID
	 */
	get _templateItemID() {
		return html`
			<div class="item__detail">
				<uo-icon class="item__detail-icon" id="hashtag"></uo-icon>

				${ this.id }
			</div>
		`;
	}

	/**
	 * Returns the HTML of the section that shows the start and end date
	 *
	 * @return {TemplateResult|undefined} The date or undefined if the date shouldn't be shown
	 */
	get _templateDate() {
		// Check if the item hasn't run yet
		if (
			isEmpty( this.startDate ) ||
			[ 'skipped' ].includes( this.statusID )
		) {
			return;
		}

		// Check if the date is a range
		const dateIsRange = ! isEmpty( this.endDate ) && this.startDate !== this.endDate;

		return html`
			<div class="item__detail">
				<uo-icon class="item__detail-icon" id="clock"></uo-icon>

				${
					dateIsRange ?
					sprintf(
						/* translators: 1 and 2 are dates */
						escapeHTML( __( '%1$s to %2$s', 'uncanny-automator' ) ),
						this.startDate,
						this.endDate
					) :
					this.startDate
				}
			</div>
		`;
	}

	/**
	 * Returns the HTML of the section that shows the status
	 *
	 * @return {TemplateResult} The status of this item
	 */
	get _templateStatus() {
		return html`
			<div class="item__detail">
				<uap-log-status status-id="${ this.statusID }"></uap-log-status>
			</div>
		`;
	}

	/**
	 * Converts fields into properties for the sidebar
	 *
	 * @return {Array} where each element is a field of the trigger or action, prepared for the sidebar
	 */
	get fieldsForSidebar() {
		// Prepare fields for sidebar
		const fieldsSidebar = [];

		this.fields.forEach( field => {
			// Get the value of the field that'll show in the sidebar
			// Actions will have a `parsed` property, which will contain the value of the tokens
			// instead of the token IDs
			let fieldValue = isDefined( field.value.parsed ) ? field.value.parsed : field.value.raw;

			// Selects will also contain a `readable` property, which will contain the text of the option
			if ( field.type !== 'file' && ! isEmpty( field.value.readable ) ) {
				fieldValue = `${ fieldValue } (${ field.value.readable })`;
			}

			fieldsSidebar.push( {
				type:  field.type,
				label: field.label,
				value: fieldValue,
				attributes: ! isEmpty( field.attributes ) ? field.attributes : {}
			} );
		} );

		return fieldsSidebar;
	}

	/**
	 * Checks whether this item was removed and the basic data is missing
	 *
	 * @return {Boolean} Whether the item is missing
	 */
	get isDataMissing() {
		return this.integrationCode === 'INTEGRATION_CODE_NOT_FOUND' ||
			[ 'TRIGGER_CODE_NOT_FOUND', 'ACTION_CODE_NOT_FOUND' ].includes( this.code );
	}
}

export const AutomatorLogItem = LogItemMixin( LitElement );
